import { useEffect, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { Section, SectionHeader } from "@/components/site/Section";
import { pathsForIndustry } from "@/content/learning-paths";
import { LearningPathCard } from "@/components/site/LearningPathCard";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";

export function LearningPathSection({ industrySlug }: { industrySlug: string }) {
  const paths = pathsForIndustry(industrySlug);
  const hash = useRouterState({ select: (s) => s.location.hash });
  const [api, setApi] = useState<CarouselApi>();
  const [snaps, setSnaps] = useState<number[]>([]);
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (hash !== "learning-path" && hash !== "#learning-path") return;
    const el = document.getElementById("learning-path");
    if (!el) return;
    const t = window.setTimeout(
      () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
      60,
    );
    return () => window.clearTimeout(t);
  }, [hash, industrySlug]);

  useEffect(() => {
    if (!api) return;
    const sync = () => {
      setSnaps(api.scrollSnapList());
      setSelected(api.selectedScrollSnap());
    };
    sync();
    api.on("select", sync);
    api.on("reInit", sync);
    return () => {
      api.off("select", sync);
      api.off("reInit", sync);
    };
  }, [api]);

  if (paths.length === 0) return null;

  return (
    <Section tone="default" id="learning-path">
      <SectionHeader eyebrow="Learning path" title="Where to start" />

      <p className="-mt-6 mb-8 text-sm text-muted-foreground">
        Six ways in. Pick the one that matches who you're developing.
      </p>

      <Carousel
        setApi={setApi}
        opts={{ align: "start", loop: false, containScroll: "trimSnaps" }}
        className="w-full"
      >
        <CarouselContent className="-ml-4 items-stretch">
          {paths.map((path) => (
            <CarouselItem key={path.slug} className="pl-4 sm:basis-1/2 lg:basis-1/3">
              <LearningPathCard path={path} />
            </CarouselItem>
          ))}
        </CarouselContent>

        <div className="mt-8 flex items-center justify-center gap-4">
          <CarouselPrevious className="static translate-y-0" />
          <div className="flex items-center gap-2">
            {snaps.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === selected || undefined}
                onClick={() => api?.scrollTo(i)}
                className={`relative after:absolute after:-inset-x-1 after:-inset-y-4 after:content-[''] h-2 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                  i === selected ? "w-6 bg-primary" : "w-2 bg-border"
                }`}
              />
            ))}
          </div>
          <CarouselNext className="static translate-y-0" />
        </div>
      </Carousel>
    </Section>
  );
}
